import {
  Box,
  Button,
  Flex,
  Heading,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import StudentPortalShell from "../../../components/student/StudentPortalShell";
import { studentPalette } from "../../../constants/studentPalette";
import { studentSurfaces } from "../../../constants/studentThemeSurfaces";
import {
  applyForOfficeHourRequest,
  getOfficeHoursHistoryRequest,
  getStudentOfficeHoursRequest,
  getUpcomingOfficeHoursRequest,
} from "../../../modules/students-modules/office-hours/Actions";
import {
  DAILY_APPLY_LIMIT,
  WEEKLY_APPLY_LIMIT,
  getOfficeHoursApplyEligibility,
  mergeOfficeHourBookings,
} from "../../../modules/students-modules/office-hours/officeHoursLimits";

const C = {
  primary: studentPalette.primary,
  secondary: studentPalette.secondary,
  border: studentSurfaces.border,
  muted: studentSurfaces.textMuted,
  paper: studentSurfaces.raised,
};

function formatRange(fromVal, toVal) {
  const a = fromVal ? dayjs(fromVal) : null;
  const b = toVal ? dayjs(toVal) : null;
  if (a?.isValid() && b?.isValid()) {
    return `${a.format("D MMM YYYY, HH:mm")} – ${b.format("HH:mm")}`;
  }
  if (a?.isValid()) return a.format("D MMM YYYY, HH:mm");
  return "—";
}

/** Seconds until `from`; negative if already passed */
function useSecondsUntil(iso) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, []);

  return useMemo(() => {
    void tick;
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    return Math.floor((t - Date.now()) / 1000);
  }, [iso, tick]);
}

function CountdownLabel({ fromIso }) {
  const sec = useSecondsUntil(fromIso);
  if (sec == null) return <Text fontSize="sm" color={C.muted}>—</Text>;
  if (sec <= 0) {
    return (
      <Text fontSize="sm" fontWeight="700" color={C.secondary}>
        Started
      </Text>
    );
  }
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const parts = [];
  if (d > 0) parts.push(`${d}d`);
  if (h > 0 || d > 0) parts.push(`${h}h`);
  parts.push(`${m}m`);
  parts.push(`${s}s`);
  return (
    <Text fontSize="sm" fontWeight="800" color={C.primary} fontVariantNumeric="tabular-nums">
      {parts.join(" ")}
    </Text>
  );
}

function OfficeHourCard({ item, mode, studentId, onApply, applyLoading, canApply }) {
  const title = item?.title || item?.name || "Office hour";
  const from = item?.from;
  const to = item?.to;
  const zoom = item?.zoom_link;
  const id = item?.id;
  const applyDisabled = applyLoading || !canApply;

  return (
    <Box
      bg={C.paper}
      borderWidth="1px"
      borderColor={C.border}
      borderRadius="xl"
      p={5}
      w="100%"
    >
      <Flex justify="space-between" align="flex-start" gap={4} flexWrap="wrap">
        <Box flex="1" minW={0}>
          <Text fontSize="xs" fontWeight="800" color={C.muted} textTransform="uppercase" letterSpacing="0.08em" mb={1}>
            {mode === "upcoming" ? "Starts in" : "Session"}
          </Text>
          <Heading as="h3" fontSize="md" fontWeight="800" color={C.primary} mb={2} noOfLines={2}>
            {title}
          </Heading>
          <Text fontSize="sm" color={C.secondary} fontWeight="600">
            {formatRange(from, to)}
          </Text>
          {mode === "upcoming" && zoom ? (
            <Button
              as="a"
              href={zoom}
              target="_blank"
              rel="noopener noreferrer"
              size="sm"
              mt={3}
              variant="outline"
              borderColor={C.primary}
              color={C.primary}
              fontWeight="800"
              alignSelf="flex-start"
              _hover={{ bg: `${C.primary}14`, borderColor: C.secondary, color: C.secondary }}
            >
              Join meeting
            </Button>
          ) : null}
        </Box>
        <VStack align="flex-end" spacing={2} flexShrink={0}>
          {mode === "upcoming" ? <CountdownLabel fromIso={from} /> : null}
          {mode === "available" && studentId != null && id != null ? (
            <Button
              size="sm"
              bg={C.primary}
              color="white"
              fontWeight="800"
              _hover={{ bg: C.secondary }}
              isLoading={applyLoading}
              isDisabled={applyDisabled}
              onClick={() => onApply?.(id)}
            >
              Apply
            </Button>
          ) : null}
        </VStack>
      </Flex>
    </Box>
  );
}

const StudentOfficeHoursPage = () => {
  const dispatch = useDispatch();
  const userData = useSelector((s) => s.user.userData);
  const studentId = userData?.id;

  const available = useSelector((s) => s.studentOfficeHours.available);
  const upcoming = useSelector((s) => s.studentOfficeHours.upcoming);
  const history = useSelector((s) => s.studentOfficeHours.history);
  const isLoadingAvailable = useSelector((s) => s.studentOfficeHours.isLoadingAvailable);
  const isLoadingUpcoming = useSelector((s) => s.studentOfficeHours.isLoadingUpcoming);
  const applyLoading = useSelector((s) => s.studentOfficeHours.applyLoading);

  useEffect(() => {
    dispatch(getStudentOfficeHoursRequest());
  }, [dispatch]);

  useEffect(() => {
    if (studentId == null) return;
    dispatch(getUpcomingOfficeHoursRequest(studentId));
    dispatch(getOfficeHoursHistoryRequest(studentId));
  }, [dispatch, studentId]);

  const eligibility = useMemo(
    () => getOfficeHoursApplyEligibility(mergeOfficeHourBookings(history, upcoming)),
    [history, upcoming]
  );

  const handleApply = (officeHourId) => {
    if (studentId == null || officeHourId == null) return;
    if (!eligibility.canApply) return;
    dispatch(applyForOfficeHourRequest(studentId, officeHourId));
  };

  const listAvailable = Array.isArray(available) ? available : [];
  const listUpcoming = Array.isArray(upcoming) ? upcoming : [];

  return (
    <StudentPortalShell>
      <Box p={{ base: 4, md: 8 }} maxW="900px">
        <Heading
          as="h1"
          fontSize={{ base: "2xl", md: "3xl" }}
          fontWeight="800"
          color={C.primary}
          fontFamily="'Tajawal','Amiri',sans-serif"
          mb={2}
        >
          One to one support
        </Heading>
        <Text fontSize="sm" color={C.muted} mb={2}>
          Browse open slots and see sessions you have joined.
        </Text>
        <Text fontSize="sm" color={C.secondary} fontWeight="600" mb={8}>
          Today {eligibility.dailyCount}/{DAILY_APPLY_LIMIT} · This week{" "}
          {eligibility.weeklyCount}/{WEEKLY_APPLY_LIMIT}
        </Text>
        {!eligibility.canApply && eligibility.reason ? (
          <Text
            fontSize="sm"
            fontWeight="700"
            color={C.primary}
            bg={`${C.primary}12`}
            borderWidth="1px"
            borderColor={`${C.primary}33`}
            borderRadius="lg"
            px={4}
            py={3}
            mb={6}
          >
            {eligibility.reason}
          </Text>
        ) : null}

        <Tabs colorScheme="red" variant="enclosed">
          <TabList borderColor={C.border}>
            <Tab fontWeight="700" color={C.secondary}>
              Available office hours
            </Tab>
            <Tab fontWeight="700" color={C.secondary}>
              Upcoming office hours
            </Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0} pt={6}>
              {isLoadingAvailable ? (
                <VStack align="stretch" spacing={4}>
                  <Skeleton height="100px" borderRadius="xl" />
                  <Skeleton height="100px" borderRadius="xl" />
                </VStack>
              ) : listAvailable.length === 0 ? (
                <Text color={C.muted} fontWeight="600">
                  No office hours are available right now.
                </Text>
              ) : (
                <VStack align="stretch" spacing={4}>
                  {listAvailable.map((item) => (
                    <OfficeHourCard
                      key={item.id ?? JSON.stringify(item)}
                      item={item}
                      mode="available"
                      studentId={studentId}
                      onApply={handleApply}
                      applyLoading={applyLoading}
                      canApply={eligibility.canApply}
                    />
                  ))}
                </VStack>
              )}
            </TabPanel>
            <TabPanel px={0} pt={6}>
              {isLoadingUpcoming ? (
                <VStack align="stretch" spacing={4}>
                  <Skeleton height="100px" borderRadius="xl" />
                  <Skeleton height="100px" borderRadius="xl" />
                </VStack>
              ) : listUpcoming.length === 0 ? (
                <Text color={C.muted} fontWeight="600">
                  You have no upcoming office hours.
                </Text>
              ) : (
                <VStack align="stretch" spacing={4}>
                  {listUpcoming.map((item) => (
                    <OfficeHourCard
                      key={item.id ?? `up-${item.from}`}
                      item={item}
                      mode="upcoming"
                    />
                  ))}
                </VStack>
              )}
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>
    </StudentPortalShell>
  );
};

export default StudentOfficeHoursPage;
